/**
 * Pure chat-stream reducers shared by both streaming transports:
 * - live mode: `chat.delta/done/error` envelopes arriving over the E2E relay;
 * - Worker SSE proxy (existing live fallback) and the demo MockDaemon stream.
 *
 * Keeping the turn mutation here (instead of inline in AppState) makes the
 * streaming state machine unit-testable without React, and guarantees the relay
 * and SSE paths render exactly the same way.
 */
import type { ChatDelta, ChatDone, ChatError } from '@luminis/protocol';
import { nowSeconds, randomId } from './id';
import type { ChatConversation, ChatTurn } from './types';

/** Which conversation/turn a stream is currently writing into. */
export interface ChatStreamTarget {
  conversationId: string;
  turnId: string;
  /** Adopted from the first ChatDelta/ChatDone when the backend assigns it. */
  messageId?: string;
  /** Monotonic delta ordering state; survives message-id adoption. */
  deltaSequence?: ChatDeltaSequence;
}

/**
 * Buffers out-of-order stream deltas until the missing sequence numbers arrive.
 * Relay replay/reconnect can deliver later chunks first; appending in arrival
 * order makes the first characters look scrambled.
 */
export interface ChatDeltaSequence {
  lastSeq: number;
  pending: Map<number, ChatDelta>;
}

export function createChatDeltaSequence(): ChatDeltaSequence {
  return { lastSeq: -1, pending: new Map() };
}

export function orderedChatDeltas(sequence: ChatDeltaSequence, delta: ChatDelta): ChatDelta[] {
  if (delta.seq <= sequence.lastSeq) return [];
  sequence.pending.set(delta.seq, delta);
  const ordered: ChatDelta[] = [];
  let next = sequence.lastSeq + 1;
  for (;;) {
    const item = sequence.pending.get(next);
    if (item === undefined) break;
    sequence.pending.delete(next);
    ordered.push(item);
    next += 1;
  }
  if (ordered.length > 0) sequence.lastSeq = next - 1;
  return ordered;
}

/**
 * Adopt the backend message id on first sight. Returns the same object when the
 * id is already known; `null` means the event belongs to another message.
 */
export function adoptChatMessageId(
  target: ChatStreamTarget,
  messageId: string | undefined,
): ChatStreamTarget | null {
  if (!messageId) return target;
  if (!target.messageId) return { ...target, messageId };
  return target.messageId === messageId ? target : null;
}

function patchTurn(
  conversations: readonly ChatConversation[],
  conversationId: string,
  turnId: string,
  updater: (turn: ChatTurn) => ChatTurn,
  now: number,
): ChatConversation[] {
  return conversations.map((conversation) => {
    if (conversation.id !== conversationId) return conversation;
    return {
      ...conversation,
      updated_at: now,
      turns: conversation.turns.map((turn) => (turn.id === turnId ? updater(turn) : turn)),
    };
  });
}

/** Append a content/reasoning delta to the streaming assistant turn. */
export function appendChatDelta(
  conversations: readonly ChatConversation[],
  target: Pick<ChatStreamTarget, 'conversationId' | 'turnId'>,
  delta: ChatDelta,
  now = nowSeconds(),
): ChatConversation[] {
  return patchTurn(
    conversations,
    target.conversationId,
    target.turnId,
    (turn) =>
      delta.kind === 'reasoning'
        ? { ...turn, reasoning: turn.reasoning + delta.delta }
        : { ...turn, content: turn.content + delta.delta },
    now,
  );
}

/** Mark the streaming assistant turn as completed and record model/usage. */
export function finishChatDone(
  conversations: readonly ChatConversation[],
  target: Pick<ChatStreamTarget, 'conversationId' | 'turnId'>,
  done: ChatDone,
  now = nowSeconds(),
): ChatConversation[] {
  return patchTurn(
    conversations,
    target.conversationId,
    target.turnId,
    (turn) => ({
      ...turn,
      // Authoritative final text wins over incrementally appended deltas: relay
      // queue replay/reconnects may deliver deltas out of order.
      ...(done.content !== undefined ? { content: done.content } : {}),
      ...(done.reasoning !== undefined ? { reasoning: done.reasoning } : {}),
      status: 'done',
      model: done.model,
      ...(done.usage ? { usage: done.usage } : {}),
    }),
    now,
  );
}

/** Mark the streaming assistant turn as failed. */
export function finishChatError(
  conversations: readonly ChatConversation[],
  target: Pick<ChatStreamTarget, 'conversationId' | 'turnId'>,
  error: ChatError,
  now = nowSeconds(),
): ChatConversation[] {
  const suffix = `${error.message}（${error.code}${error.retryable ? '，可重试' : ''}）`;
  return patchTurn(
    conversations,
    target.conversationId,
    target.turnId,
    (turn) => ({ ...turn, status: 'error', error: suffix }),
    now,
  );
}

/** Mark the streaming assistant turn as locally stopped. */
export function finishChatStopped(
  conversations: readonly ChatConversation[],
  target: Pick<ChatStreamTarget, 'conversationId' | 'turnId'>,
  now = nowSeconds(),
): ChatConversation[] {
  return patchTurn(
    conversations,
    target.conversationId,
    target.turnId,
    (turn) => ({ ...turn, status: 'stopped' }),
    now,
  );
}

/** Minimal execution shape needed to render a tool result in the chat. */
export interface ExecutionResultLike {
  exec_id: string;
  tool: string;
  status: 'running' | 'ok' | 'failed';
  exit_code?: number | null;
  duration_ms?: number;
  stdout: string;
  stderr: string;
}

/**
 * Append the daemon's tool execution result to the active conversation as a
 * system turn, so the chat shows what an approved command actually returned
 * (the pause/resume of the assistant turn itself is still a later milestone).
 */
export function appendExecutionResultTurn(
  conversations: readonly ChatConversation[],
  conversationId: string,
  exec: ExecutionResultLike,
  now = nowSeconds(),
): ChatConversation[] {
  if (exec.status === 'running') return [...conversations];
  const statusLabel = exec.status === 'ok' ? '执行成功' : '执行失败';
  const exit =
    exec.exit_code !== undefined && exec.exit_code !== null ? ` · exit ${exec.exit_code}` : '';
  const duration = exec.duration_ms !== undefined ? ` · ${Math.round(exec.duration_ms)} ms` : '';
  const parts = [`**执行结果** · \`${exec.tool}\` · ${statusLabel}${exit}${duration}`];
  const stdout = exec.stdout.trim();
  const stderr = exec.stderr.trim();
  if (stdout.length > 0) {
    parts.push('', '```', stdout.slice(-4000), '```');
  }
  if (stderr.length > 0) {
    parts.push('', 'stderr:', '```', stderr.slice(-2000), '```');
  }
  const turn: ChatTurn = {
    id: randomId('turn_'),
    role: 'system',
    content: parts.join('\n'),
    reasoning: '',
    created_at: now,
    status: 'done',
  };
  let found = false;
  const next = conversations.map((conversation) => {
    if (conversation.id !== conversationId) return conversation;
    found = true;
    return {
      ...conversation,
      updated_at: now,
      turns: [...conversation.turns, turn],
    };
  });
  return found ? next : [...conversations];
}
